/**
 * Spinner Component
 * Loading indicator
 */

import { HTMLAttributes, forwardRef } from 'react'
import { clsx } from 'clsx'

export type SpinnerSize = 'sm' | 'md' | 'lg' | 'xl'

export interface SpinnerProps extends HTMLAttributes<HTMLDivElement> {
  size?: SpinnerSize
}

const sizeStyles: Record<SpinnerSize, string> = {
  sm: 'h-4 w-4',
  md: 'h-6 w-6',
  lg: 'h-8 w-8',
  xl: 'h-12 w-12',
}

export const Spinner = forwardRef<HTMLDivElement, SpinnerProps>(
  ({ size = 'md', className, ...props }, ref) => {
    return (
      <div ref={ref} className={clsx('inline-block', className)} {...props}>
        <svg
          className={clsx('animate-spin text-primary-600', sizeStyles[size])}
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          ></circle>
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          ></path>
        </svg>
      </div>
    )
  },
)

Spinner.displayName = 'Spinner'

export const SpinnerOverlay = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  ({ className, children, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={clsx(
          'fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50',
          className,
        )}
        {...props}
      >
        <div className="bg-white rounded-lg p-6 flex flex-col items-center gap-4">
          <Spinner size="lg" />
          {children && <p className="text-gray-700">{children}</p>}
        </div>
      </div>
    )
  },
)

SpinnerOverlay.displayName = 'SpinnerOverlay'
